// src/pages/FicheDetailPage.jsx
import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as LucideIcons from "lucide-react";
import { fichesPratiquesFR, fichesPratiquesEN } from "../data/fichesPratiques";

// Composant pour afficher les icônes Lucide
const FicheIcon = ({ iconName, className = "" }) => {
  const IconComponent = LucideIcons[iconName];
  
  if (!IconComponent) {
    return <LucideIcons.HelpCircle className={className} />;
  }
  
  return <IconComponent className={className} />;
};

const FicheDetailPage = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n?.language || "fr").slice(0, 2);
  const { ficheId } = useParams();

  // Le contenu éditorial existe en FR et EN. L'allemand utilise l'anglais
  // comme fallback tant qu'un dataset fichesPratiquesDE n'est pas disponible.
  const dataset = useMemo(() => {
    if (lang === "fr") return fichesPratiquesFR;
    const frMap = new Map(fichesPratiquesFR.map((f) => [f.slug, f]));
    const merged = new Map(frMap);
    fichesPratiquesEN.forEach((enFiche) => {
      const base = frMap.get(enFiche.slug) || {};
      merged.set(enFiche.slug, { ...base, ...enFiche });
    });
    return Array.from(merged.values());
  }, [lang]);

  const fiche = useMemo(
    () => dataset.find((f) => f.slug === ficheId),
    [dataset, ficheId]
  );


  if (!fiche) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t("ficheDetailPage.notFoundTitle")}
          </h1>
          <Link to="/fiches-pratiques" className="text-indigo-600 hover:underline">
            {t("ficheDetailPage.backToGuides")}
          </Link>
        </div>
      </div>
    );
  }

  const steps = Array.isArray(fiche.steps) ? fiche.steps : [];
  const related = Array.isArray(fiche.related_links) ? fiche.related_links : [];

  // Suggestions : prendre 3 autres fiches du même dataset
  const suggestions = dataset.filter((f) => f.slug !== fiche.slug).slice(0, 3);

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Navigation de retour */}
      <div className="mb-6">
        <Link
          to="/fiches-pratiques"
          className="text-indigo-600 hover:underline flex items-center gap-2"
        >
          <span>←</span> {t("ficheDetailPage.backToGuides")}
        </Link>
      </div>

      {/* En-tête de la fiche */}
      <div className="mb-8">
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0">
            <FicheIcon iconName={fiche.icon} className="w-12 h-12 text-gray-900" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {fiche.title}
            </h1>
            <p className="text-lg text-gray-600">{fiche.description}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Contenu principal */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="font-semibold text-xl text-gray-900 mb-4">{t("ficheDetailPage.steps")}</h2>
            <div className="space-y-4">
              {steps.length > 0 ? (
                steps.map((step, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </div>
                    <p className="text-gray-700 leading-relaxed">{step}</p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">—</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="font-semibold text-lg text-gray-900 mb-4">{t("ficheDetailPage.usefulLinks")}</h3>
            <div className="space-y-3">
              {related.length > 0 ? (
                related.map((link, index) => (
                  <div key={index}>
                    {link?.href?.startsWith("mailto:") ? (
                      <a href={link.href} className="text-indigo-600 hover:underline block">
                        {link.label}
                      </a>
                    ) : link?.href === "#" ? (
                      <span className="text-gray-400 block">
                        {link.label} ({t("ficheDetailPage.comingSoon")})
                      </span>
                    ) : link?.href?.startsWith("/") ? (
                      <Link to={link.href} className="text-indigo-600 hover:underline block">
                        {link.label}
                      </Link>
                    ) : link?.href ? (
                      <a
                        href={link.href}
                        className="text-indigo-600 hover:underline block"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <span className="text-gray-400 block">{link?.label || "—"}</span>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">—</p>
              )}
            </div>
          </div>

          {/* Suggestions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
            <h3 className="font-semibold text-lg text-gray-900 mb-4">{t("ficheDetailPage.otherGuides")}</h3>
            <div className="space-y-3">
              {suggestions.map((s) => (
                <Link
                  key={s.slug}
                  to={`/fiche/${s.slug}`}
                  className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 transition-colors"
                >
                  <FicheIcon iconName={s.icon} className="w-5 h-5 text-gray-700 flex-shrink-0" />
                  <span className="text-sm text-gray-700 hover:text-indigo-600">
                    {s.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FicheDetailPage;